<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  getMe,
  getProject,
  getProjectOverview,
  getProjectWeather,
  listActivity,
  listLibraryDocuments,
  listNotifications,
  refreshProjectAiStatus,
  updateProjectApprovalPolicy,
  updateProjectNotificationRecipients,
} from "@/api/client";
import { PROJECT_FEATURES } from "@/modules";
import EmptyState from "@/components/EmptyState.vue";
import PageLayout from "@/components/PageLayout.vue";

// Heroicon (outline) paths for the dashboard empty states.
const ICON_CHECK_CIRCLE =
  "M9 12.75 11.25 15 15 9.75m6 2.25a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z";
const ICON_CLOCK = "M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z";

const route = useRoute();
const queryClient = useQueryClient();
const projectId = computed(() => route.params.projectId as string);

const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
const project = computed(() =>
  (me.value?.projects ?? []).find((p) => p.id === projectId.value),
);

// The project's feature cards (everything except the dashboard itself).
const cards = computed(() =>
  PROJECT_FEATURES.filter((f) => f.name !== "project-dashboard"),
);

// --- Overview: KPIs + AI status + needs-attention + recent activity ---
const { data: overview } = useQuery({
  queryKey: ["project-overview", projectId],
  queryFn: () => getProjectOverview(projectId.value),
  enabled: () => !!projectId.value,
});
const { data: attention } = useQuery({
  queryKey: ["notifications", projectId],
  queryFn: () => listNotifications(projectId.value),
  enabled: () => !!projectId.value,
});
const { data: activity } = useQuery({
  queryKey: ["activity", projectId],
  queryFn: () => listActivity(projectId.value),
  enabled: () => !!projectId.value,
});
const recentActivity = computed(() => (activity.value ?? []).slice(0, 8));

// Documents the signed-in user still needs to read + acknowledge — surfaced as
// a dashboard prompt, not just inside the Documents page.
const { data: libraryDocs } = useQuery({
  queryKey: ["library-documents", projectId],
  queryFn: () => listLibraryDocuments(projectId.value),
  enabled: () => !!projectId.value,
});
const docsNeedingAck = computed(() =>
  (libraryDocs.value ?? []).filter(
    (d) => d.acknowledgement_required && !d.acknowledged_by_me,
  ),
);

// Live/forecast weather for the project's site location (null if no location
// set). Refreshed every 30 min; distinct from the diary's recorded weather.
const { data: weather } = useQuery({
  queryKey: ["project-weather", projectId],
  queryFn: () => getProjectWeather(projectId.value),
  enabled: () => !!projectId.value,
  staleTime: 30 * 60 * 1000,
  refetchInterval: 30 * 60 * 1000,
});
const dayName = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", { weekday: "short" });

const today = new Date().toLocaleDateString("en-GB", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
});

const KPIS: Array<{ key: keyof NonNullable<typeof overview.value>["kpis"]; label: string; route?: string; tone?: "warn" | "danger" }> = [
  { key: "open_defects", label: "Open defects", route: "defects", tone: "danger" },
  { key: "open_snags", label: "Open snags", route: "defects", tone: "warn" },
  { key: "open_observations", label: "Close calls open", route: "observations" },
  { key: "inspections_pending", label: "Inspections to approve", route: "check-sheets", tone: "warn" },
  { key: "diaries_awaiting_approval", label: "Diaries to approve", route: "site-diaries", tone: "warn" },
  { key: "diaries_last_7_days", label: "Diaries this week", route: "site-diaries" },
];

// Regenerate the AI status when it's stale/missing (throttled server-side by a
// 12h TTL, so this fires at most once a day per project).
const aiBusy = ref(false);
async function refreshAi(): Promise<void> {
  aiBusy.value = true;
  try {
    await refreshProjectAiStatus(projectId.value);
    await queryClient.invalidateQueries({ queryKey: ["project-overview", projectId] });
  } finally {
    aiBusy.value = false;
  }
}
watch(overview, (o) => {
  if (o && o.ai_status.stale && !aiBusy.value) void refreshAi();
});

function whenAgo(iso: string | null): string {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short",
  });
}
function dueLabel(iso: string, overdue: boolean): string {
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (overdue) return `overdue · ${fmtDate(iso)}`;
  if (days <= 0) return "due today";
  if (days === 1) return "due tomorrow";
  return `in ${days}d · ${fmtDate(iso)}`;
}

// --- approval routing (brief §1.5) ---
// Which role signs off each document type on this project. Default = CRE.
const { data: projectDetail } = useQuery({
  queryKey: ["project", projectId],
  queryFn: () => getProject(projectId.value),
  enabled: () => !!projectId.value,
});
const APPROVAL_DOCS = [
  { key: "site_diary", label: "Site diaries" },
  { key: "check_sheet", label: "Quality check sheets" },
] as const;
const policy = ref<Record<string, string>>({});
watch(
  projectDetail,
  (p) => {
    policy.value = {
      site_diary: p?.approval_policy?.site_diary ?? "cre",
      check_sheet: p?.approval_policy?.check_sheet ?? "cre",
    };
  },
  { immediate: true },
);
const policyBusy = ref(false);
const policyMsg = ref<string | null>(null);
async function saveApprovalPolicy(): Promise<void> {
  policyBusy.value = true;
  policyMsg.value = null;
  try {
    await updateProjectApprovalPolicy(projectId.value, { ...policy.value });
    await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    policyMsg.value = "Approval routing saved.";
  } catch (e) {
    policyMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    policyBusy.value = false;
  }
}

// --- approver email recipients (brief §1.5) ---
// Comma-separated addresses notified when a document is submitted for approval.
const APPROVER_ROLES = [
  { key: "cre", label: "CRE" },
  { key: "project_manager", label: "Project Manager" },
] as const;
const recipients = ref<Record<string, string>>({ cre: "", project_manager: "" });
watch(
  projectDetail,
  (p) => {
    const e = p?.notification_emails ?? {};
    recipients.value = {
      cre: (e.cre ?? []).join(", "),
      project_manager: (e.project_manager ?? []).join(", "),
    };
  },
  { immediate: true },
);
const recipBusy = ref(false);
const recipMsg = ref<string | null>(null);
function _parseEmails(raw: string): string[] {
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}
async function saveRecipients(): Promise<void> {
  recipBusy.value = true;
  recipMsg.value = null;
  try {
    await updateProjectNotificationRecipients(projectId.value, {
      cre: _parseEmails(recipients.value.cre),
      project_manager: _parseEmails(recipients.value.project_manager),
    });
    await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    recipMsg.value = "Recipients saved.";
  } catch (e) {
    recipMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    recipBusy.value = false;
  }
}
</script>

<template>
  <PageLayout wide>
    <div class="grid gap-8">
      <header>
        <p class="text-sm text-neutral-400">
          Today on
        </p>
        <h1 class="text-2xl font-extrabold text-neutral-900">
          {{ project?.name ?? "Project" }}
        </h1>
        <p class="mt-1 text-neutral-500">
          <span v-if="project?.code">{{ project.code }} · </span>
          {{ today }}
        </p>
      </header>

      <!-- Documents needing your acknowledgement -->
      <RouterLink
        v-if="docsNeedingAck.length"
        :to="{ name: 'documents', params: { projectId } }"
        class="flex items-center justify-between gap-3 rounded-xl border border-semantic-warning/40 bg-semantic-warning/5 p-4 hover:bg-semantic-warning/10"
      >
        <div class="flex items-center gap-3">
          <svg
            class="h-5 w-5 shrink-0 text-semantic-warning"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m0 3.75h.008M10.34 3.94 1.82 18.18A1.5 1.5 0 0 0 3.12 20.5h17.76a1.5 1.5 0 0 0 1.3-2.32L13.66 3.94a1.5 1.5 0 0 0-2.6 0Z"
            />
          </svg>
          <div>
            <p class="text-sm font-semibold text-neutral-900">
              {{ docsNeedingAck.length }}
              {{ docsNeedingAck.length === 1 ? "document needs" : "documents need" }}
              your acknowledgement
            </p>
            <p class="text-xs text-neutral-500">
              Read and confirm you understand — e.g. master RAMS, policies, standards.
            </p>
          </div>
        </div>
        <span class="shrink-0 text-sm font-medium text-brand-primary">Review →</span>
      </RouterLink>

      <!-- AI project status -->
      <section
        class="rounded-xl border border-brand-primary/20 bg-brand-primary/5 p-5"
      >
        <div class="mb-2 flex items-center justify-between gap-3">
          <h3 class="flex items-center gap-2 text-sm font-semibold text-brand-primary">
            <svg
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5 10.1 10.9 5.5 9l4.6-1.4z" />
            </svg>
            AI project status
          </h3>
          <button
            type="button"
            class="text-xs text-neutral-400 hover:text-brand-primary disabled:opacity-50"
            :disabled="aiBusy"
            @click="refreshAi"
          >
            {{ aiBusy ? "Refreshing…" : "Refresh" }}
          </button>
        </div>
        <p
          v-if="overview?.ai_status.text"
          class="text-sm leading-relaxed text-neutral-800"
        >
          {{ overview.ai_status.text }}
        </p>
        <p
          v-else
          class="text-sm text-neutral-500"
        >
          {{ aiBusy ? "Writing a status snapshot from the project data…" : "No status yet." }}
        </p>
        <p
          v-if="overview?.ai_status.generated_at"
          class="mt-2 text-xs text-neutral-400"
        >
          Updated {{ whenAgo(overview.ai_status.generated_at) }}
        </p>
      </section>

      <!-- KPI strip -->
      <section
        v-if="overview"
        class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
      >
        <RouterLink
          v-for="k in KPIS"
          :key="k.key"
          :to="k.route ? { name: k.route, params: { projectId } } : {}"
          class="rounded-xl border border-neutral-200 bg-neutral-0 p-4 transition-shadow hover:shadow-md"
        >
          <p
            class="text-2xl font-bold"
            :class="overview.kpis[k.key] === 0
              ? 'text-neutral-900'
              : k.tone === 'danger'
                ? 'text-semantic-danger'
                : k.tone === 'warn'
                  ? 'text-semantic-warning'
                  : 'text-neutral-900'"
          >
            {{ overview.kpis[k.key] }}
          </p>
          <p class="text-xs text-neutral-500">
            {{ k.label }}
          </p>
        </RouterLink>
      </section>

      <!-- Watch this week + Site weather -->
      <div
        v-if="overview"
        class="grid gap-6 lg:grid-cols-3"
      >
        <section class="flex flex-col lg:col-span-2">
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Watch this week
          </h3>
          <div class="grow rounded-xl border border-neutral-200 bg-neutral-0 p-2">
            <EmptyState
              v-if="!overview.watch.length"
              tone="positive"
              image="/nothing-this-week.svg"
              title="Nothing due this week"
              message="You're on top of due dates and deadlines."
            />
            <RouterLink
              v-for="(w, i) in overview.watch"
              :key="i"
              :to="w.target_id
                ? { name: w.target_route, params: { projectId, id: w.target_id, defectId: w.target_id } }
                : { name: w.target_route, params: { projectId } }"
              class="flex items-center gap-3 rounded-lg p-3 hover:bg-neutral-50"
            >
              <span
                class="h-2 w-2 shrink-0 rounded-full"
                :class="w.overdue ? 'bg-semantic-danger' : 'bg-semantic-warning'"
              />
              <span class="min-w-0 grow truncate text-sm text-neutral-900">
                {{ w.label }}
                <span
                  v-if="w.kind === 'milestone'"
                  class="ml-1 rounded bg-neutral-100 px-1.5 text-xs text-neutral-500"
                >milestone</span>
              </span>
              <span
                class="shrink-0 text-xs font-medium"
                :class="w.overdue ? 'text-semantic-danger' : 'text-semantic-warning'"
              >
                {{ dueLabel(w.date, w.overdue) }}
              </span>
            </RouterLink>
          </div>
        </section>

        <section
          v-if="weather?.current || overview.weather"
          class="flex flex-col"
        >
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Site weather
          </h3>
          <!-- Live forecast (when a site location is set) -->
          <div
            v-if="weather?.current"
            class="grow rounded-xl border border-neutral-200 bg-neutral-0 p-5"
          >
            <div class="flex items-center gap-3">
              <span class="text-3xl leading-none">{{ weather.current.emoji }}</span>
              <div>
                <p class="text-lg font-semibold text-neutral-900">
                  {{ weather.current.temperature != null
                    ? Math.round(weather.current.temperature) + "°C" : "—" }}
                  <span class="font-normal text-neutral-500">
                    · {{ weather.current.label }}</span>
                </p>
                <p class="text-xs text-neutral-400">
                  {{ weather.location_name }}<span
                    v-if="weather.current.wind_speed != null"
                  > · wind {{ Math.round(weather.current.wind_speed) }} mph</span>
                </p>
              </div>
            </div>
            <div
              v-if="weather.forecast.length"
              class="mt-4 flex justify-between border-t border-neutral-100 pt-3"
            >
              <div
                v-for="d in weather.forecast.slice(0, 7)"
                :key="d.date"
                class="flex flex-col items-center gap-0.5 text-center"
                :title="d.label"
              >
                <span class="text-xs text-neutral-400">{{ dayName(d.date) }}</span>
                <span class="text-lg leading-none">{{ d.emoji }}</span>
                <span class="text-xs font-medium text-neutral-700">
                  {{ d.temp_max != null ? Math.round(d.temp_max) + "°" : "—" }}
                </span>
                <span
                  v-if="d.precip_prob != null && d.precip_prob >= 30"
                  class="text-[10px] text-brand-primary"
                >{{ d.precip_prob }}%</span>
              </div>
            </div>
            <p class="mt-3 text-xs text-neutral-400">
              Live forecast · Open-Meteo
            </p>
          </div>
          <!-- Recorded (diary) weather, when there's no live location -->
          <div
            v-else-if="overview.weather"
            class="grow rounded-xl border border-neutral-200 bg-neutral-0 p-5"
          >
            <p class="text-lg font-semibold text-neutral-900">
              {{ overview.weather.conditions ?? "—" }}
              <span
                v-if="overview.weather.temperature"
                class="text-neutral-500"
              > · {{ overview.weather.temperature }}°C</span>
            </p>
            <p
              v-if="overview.weather.impact"
              class="mt-1 text-sm text-neutral-500"
            >
              Impact: {{ overview.weather.impact }}
            </p>
            <p class="mt-2 text-xs text-neutral-400">
              Recorded on the diary on {{ fmtDate(overview.weather.diary_date) }} ·
              <RouterLink
                :to="{ name: 'settings', params: { projectId } }"
                class="text-brand-primary hover:underline"
              >
                set a location
              </RouterLink>
              for a live forecast
            </p>
          </div>
        </section>
      </div>

      <!-- Needs attention + Recent activity -->
      <div class="grid gap-6 lg:grid-cols-2">
        <section class="flex flex-col">
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Needs your attention
          </h3>
          <div class="grow rounded-xl border border-neutral-200 bg-neutral-0 p-2">
            <EmptyState
              v-if="!attention?.length"
              tone="positive"
              :icon="ICON_CHECK_CIRCLE"
              title="You're all caught up"
              message="Nothing is waiting on you right now."
            />
            <RouterLink
              v-for="(n, i) in (attention ?? [])"
              :key="i"
              :to="{ name: n.target_route, params: { projectId, id: n.target_id,
                                                     defectId: n.target_id, observationId: n.target_id } }"
              class="flex items-start gap-3 rounded-lg p-3 hover:bg-neutral-50"
            >
              <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-semantic-warning" />
              <span class="min-w-0">
                <span class="block text-sm font-medium text-neutral-900">{{ n.title }}</span>
                <span class="block truncate text-xs text-neutral-500">
                  {{ n.detail }}<span v-if="n.since"> · {{ whenAgo(n.since) }}</span>
                </span>
              </span>
            </RouterLink>
          </div>
        </section>

        <section class="flex flex-col">
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
            Recent activity
          </h3>
          <div class="grow rounded-xl border border-neutral-200 bg-neutral-0 p-2">
            <EmptyState
              v-if="!recentActivity.length"
              :icon="ICON_CLOCK"
              title="No activity yet"
              message="Updates appear here as the team works on site."
            />
            <div
              v-for="a in recentActivity"
              :key="a.id"
              class="flex items-start gap-3 p-3"
            >
              <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-neutral-300" />
              <span class="min-w-0">
                <span class="block text-sm text-neutral-900">{{ a.title }}</span>
                <span class="block truncate text-xs text-neutral-500">
                  {{ a.actor_name }} · {{ whenAgo(a.occurred_at) }}
                </span>
              </span>
            </div>
          </div>
        </section>
      </div>

      <section>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Quality &amp; Inspection
        </h3>
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <RouterLink
            v-for="f in cards"
            :key="f.name"
            :to="{ name: f.name, params: { projectId } }"
            class="group rounded-xl border border-neutral-200 bg-neutral-0 p-5 transition-shadow hover:shadow-md"
          >
            <div class="flex items-start gap-3">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary"
              >
                <svg
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path :d="f.icon" />
                </svg>
              </span>
              <div class="min-w-0">
                <h4 class="font-semibold">
                  {{ f.label }}
                </h4>
                <p class="mt-1 text-sm text-neutral-500">
                  {{ f.blurb }}
                </p>
              </div>
            </div>
          </RouterLink>
        </div>
      </section>

      <!-- Approval routing (brief §1.5) -->
      <section>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Approval routing
        </h3>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-5">
          <p class="mb-4 text-sm text-neutral-500">
            Who signs off each document type on this project. Submitted records
            are routed to the chosen role for approval.
          </p>
          <div class="grid gap-3 sm:max-w-md">
            <label
              v-for="doc in APPROVAL_DOCS"
              :key="doc.key"
              class="flex items-center justify-between gap-4"
            >
              <span class="text-sm text-neutral-700">{{ doc.label }}</span>
              <select
                v-model="policy[doc.key]"
                class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
              >
                <option value="cre">CRE</option>
                <option value="project_manager">Project Manager</option>
              </select>
            </label>
          </div>
          <div class="mt-4 flex items-center gap-3">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="policyBusy"
              @click="saveApprovalPolicy"
            >
              {{ policyBusy ? "Saving…" : "Save routing" }}
            </button>
            <span
              v-if="policyMsg"
              class="text-sm text-neutral-500"
            >{{ policyMsg }}</span>
          </div>
        </div>
      </section>

      <!-- Approver email recipients (brief §1.5) -->
      <section>
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Approval notifications
        </h3>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-5">
          <p class="mb-4 text-sm text-neutral-500">
            Email addresses notified when a document is submitted for that
            role's approval. Comma-separated; leave blank for none.
          </p>
          <div class="grid gap-3 sm:max-w-xl">
            <label
              v-for="r in APPROVER_ROLES"
              :key="r.key"
              class="grid gap-1"
            >
              <span class="text-sm text-neutral-700">{{ r.label }}</span>
              <input
                v-model="recipients[r.key]"
                type="text"
                placeholder="name@company.com, other@company.com"
                class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
              >
            </label>
          </div>
          <div class="mt-4 flex items-center gap-3">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
              :disabled="recipBusy"
              @click="saveRecipients"
            >
              {{ recipBusy ? "Saving…" : "Save recipients" }}
            </button>
            <span
              v-if="recipMsg"
              class="text-sm text-neutral-500"
            >{{ recipMsg }}</span>
          </div>
        </div>
      </section>
    </div>
  </PageLayout>
</template>
